import { useImperativeHandle, useRef } from "react";

import {
  FieldDefinitionCustomType,
  FieldDefinitionFieldType,
  FieldImperativeHandle,
  FieldSizeType,
} from "@/components/@types";
import { Skeleton } from "@/components/ui/skeleton";

function FieldCustomValue<T extends FieldDefinitionCustomType<T>>({
  value,
  store,
  events,
  ref,
}: FieldDefinitionFieldType<FieldDefinitionCustomType<T>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        // Try to focus the first focusable element within the custom field
        const focusable = containerRef.current?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) as HTMLElement | null;
        focusable?.focus();
      },
      unsetFocus: () => {
        const focusable = containerRef.current?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) as HTMLElement | null;
        focusable?.blur();
      },
    }),
    [],
  );

  const component = value(store, events);
  return <div ref={containerRef}>{component}</div>;
}

function FieldCustomSkeleton({ size }: { size?: FieldSizeType }) {
  return (
    <Skeleton
      variant="text"
      level="body-md"
      style={{
        width: "75%",
      }}
    />
  );
}

export function FieldCustom<T extends FieldDefinitionCustomType<T>>({
  isLoading,
  size,
  ref,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionCustomType<T>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  if (isLoading) {
    return <FieldCustomSkeleton size={size} />;
  }

  return <FieldCustomValue {...props} size={size} ref={ref} />;
}
